import type { ViewSize } from '@mimir/client-common/util/premiere';
import {
  getDialogChoices, getViewSize,
  setViewSize, getLastSelectedFormIds,
  setLastSelectedFormId,
  isValidItemTypeForLastSelectedFormIds,
  type LastSelectedFormIds,
} from '../util/localStorage';
import type { Bookmark, BookmarkViewModel, EditBookmarkParams, UserSettings, Theme, Language } from '@mimir/common/dto/userSettingsModels';
import type { ParsedFilter } from '@mimir/common/filters/filter-util';
import type { DialogChoices } from '../model/dialogChoices';
import { generate as shortid } from 'shortid';
import { saveUserSettings as apiSaveUserSettings } from '@mimir/web/util/api';
import { logErrorAndShowToast } from '../util/uiError';
import type { Filter} from '@mimir/common/filters/Filters';
import { getParsedFilters } from '@mimir/common/filters/Filters';
import { setDialogChoices as saveDialogChoicesInLocalStorage } from '@mimir/web/util/localStorage';
import { vueSet, reactive } from '@mimir/web/compat/vueCompat';
import type { SearchItemType } from '@mimir/common/dto/itemTypes';
import type { SubtitleDefaultOptions } from '@mimir/common/dto/userSettingsModels';

interface UserSettingsState {
  viewSize: ViewSize;
  bookmarkModels: BookmarkViewModel[],
  lastSeenChangelogId: string | null,
  workFolderId: string | null,
  // TODO(dorjoy): Remove when removing 'showSetFiltersAsDefault' feature flag
  defaultFilters: ParsedFilter[],
  dismissedNotifications: Record<string, boolean>,
  showRenameSubclipDialog: boolean,
  showSequenceParts: boolean,
  dialogChoices: DialogChoices,
  language: Language,
  theme: Theme,
  lastSelectedFormIds: LastSelectedFormIds,
  subtitlePreference: SubtitleDefaultOptions,
  showPopups: boolean,
  hasBootstrapped: boolean,
}

export const userSettings: UserSettingsState = reactive({
  viewSize: getViewSize(),
  bookmarkModels: [],
  workFolderId: null,
  // TODO(dorjoy): Remove when removing 'showSetFiltersAsDefault' feature flag
  defaultFilters: [],
  dismissedNotifications: {},
  markedAsReadUserNotifications: {},
  lastSeenChangelogId: null,
  showRenameSubclipDialog: false,
  showSequenceParts: true,
  dialogChoices: getDialogChoices(),
  language: 'en',
  theme: 'dark',
  lastSelectedFormIds: getLastSelectedFormIds(),
  subtitlePreference: 'Off',
  showPopups: true,
  /**
   * Defines if the user is loading the app for the first time.
   */
  hasBootstrapped: false,
});

export function setHasBootstrappedUserSettings(val: boolean) {
  userSettings.hasBootstrapped = val;
}

export function saveUserSettingsViewSize(size: ViewSize) {
  userSettings.viewSize = size;
  setViewSize(size);
}

function setLastSeenChangelogId(val: string | null) {
  userSettings.lastSeenChangelogId = val;
}

function addBookmark(bookmark: BookmarkViewModel) {
  userSettings.bookmarkModels.push(bookmark);
}

function editBookmark(params: EditBookmarkParams) {
  const bookmark = userSettings.bookmarkModels.find(bm => bm.id === params.id);
  if (bookmark) {
    bookmark.icon = params.icon;
    bookmark.title = params.title;
    bookmark.path = params.path;
    bookmark.folderTreePath = params.folderTreePath;
  }
}

export function setWorkFolderId(folderId: string | null) {
  userSettings.workFolderId = folderId;
}


export function saveWorkFolderId(folderId: string | null) {
  setWorkFolderId(folderId);
  void saveUserSettings();
}

export function setShowRenameSubclipDialog(val: boolean) {
  userSettings.showRenameSubclipDialog = val;
}

function setDefaultFilters(filters: ParsedFilter[]) {
  userSettings.defaultFilters = filters;
}

export function dismissNotification(key: string) {
  vueSet(userSettings.dismissedNotifications, key, true);
}

function showSequenceParts(show: boolean) {
  userSettings.showSequenceParts = show;
}

function setLanguage(language: Language) {
  userSettings.language = language;
}

function setTheme(theme: Theme) {
  userSettings.theme = theme;
}

function setPopups(show: boolean) {
  userSettings.showPopups = show;
}

export function saveLastSelectedFormId(id : string, type: SearchItemType) {
  if (isValidItemTypeForLastSelectedFormIds(type)) {
    userSettings.lastSelectedFormIds[type] = id;
    setLastSelectedFormId(id, type);
  }
}

/**
 * For item creation dialogs to remember the last selected form.
 * @param type The search item type
 * @returns The last selected form id or null if none is set
 */
export function getLastSelectedFormId(type: SearchItemType) {
  if (!isValidItemTypeForLastSelectedFormIds(type)) return null;
  return userSettings.lastSelectedFormIds[type];
}

function setSubtitle(subtitleOption: SubtitleDefaultOptions) {
  userSettings.subtitlePreference = subtitleOption;
}

export function setUserSettings(settings: UserSettings) {
  const newBookmarks: BookmarkViewModel[] = settings.bookmarks.map(b => {
    return {
      id: shortid(),
      type: 'user',
      ...b,
    };
  });
  userSettings.bookmarkModels.splice(0, userSettings.bookmarkModels.length, ...newBookmarks);
  setWorkFolderId(settings.workFolderId ?? null);
  setDefaultFilters(settings.defaultFilters ?? []);
  userSettings.dismissedNotifications = settings.dismissedNotifications ?? {};
  setLastSeenChangelogId(settings.lastSeenChangelogId ?? null);
  setShowRenameSubclipDialog(settings.showRenameSubclipDialog ?? false);
  showSequenceParts(settings.showSequenceParts ?? true);
  setLanguage(settings.language ?? 'en');
  setTheme(settings.theme ?? 'os');
  setSubtitle(settings.subtitlePreference ?? 'Off');
  setPopups(settings.showPopups ?? true);
}

export async function saveUserSettings() {
  try {
    const workFolderId = userSettings.workFolderId !== null ? userSettings.workFolderId : undefined;
    const bookmarks: Bookmark[] = userSettings.bookmarkModels.map(model => {
      return {
        id: model.id,
        title: model.title,
        icon: model.icon,
        path: model.path,
        isFilterBookmark: model.isFilterBookmark,
        folderTreePath: model.folderTreePath,
        parentFolder: model.parentFolder,
      };
    });
    await apiSaveUserSettings({
      bookmarks,
      // Todo: Pass bookmarksFolders
      bookmarksFolders: undefined,
      workFolderId,
      defaultFilters: userSettings.defaultFilters,
      dismissedNotifications: userSettings.dismissedNotifications,
      lastSeenChangelogId: userSettings.lastSeenChangelogId ?? undefined,
      showRenameSubclipDialog: userSettings.showRenameSubclipDialog ?? false,
      showSequenceParts: userSettings.showSequenceParts,
      language: userSettings.language,
      theme: userSettings.theme,
      subtitlePreference: userSettings.subtitlePreference,
      showPopups: userSettings.showPopups,
    });
  } catch (err) {
    logErrorAndShowToast('Failed to save user settings, please try again later.', err, 'ex in saveUserSettings');
  }
}

export function dismissAndSaveNotification(key: string) {
  dismissNotification(key);
  void saveUserSettings();
}

export function seenLastChangelogId(latestLogId: string) {
  setLastSeenChangelogId(latestLogId);
  void saveUserSettings();
}

export async function addAndSaveBookmark(bookmark: BookmarkViewModel) {
  addBookmark(bookmark);
  await saveUserSettings();
}

export async function editAndSaveBookmark(params: EditBookmarkParams) {
  editBookmark(params);
  await saveUserSettings();
}

export async function saveDefaultFilters(filters: Filter<any>[]) {
  setDefaultFilters(getParsedFilters(filters));
  await saveUserSettings();
}

export function saveShowSequenceParts(show: boolean) {
  showSequenceParts(show);
  void saveUserSettings();
}

export function saveDialogChoices(choices: DialogChoices) {
  userSettings.dialogChoices = choices;
  saveDialogChoicesInLocalStorage(choices);
}

function deleteBookmark(bookmark: BookmarkViewModel) {
  const index = userSettings.bookmarkModels.findIndex(b => b.id === bookmark.id);
  if (index >= 0) {
    userSettings.bookmarkModels.splice(index, 1);
  }
}

export async function deleteAndSaveBookmark(bookmark: BookmarkViewModel) {
  deleteBookmark(bookmark);
  await saveUserSettings();
}

export async function saveLanguage(language: Language) {
  setLanguage(language);
  await saveUserSettings();
}

export async function saveTheme(theme: Theme) {
  setTheme(theme);
  await saveUserSettings();
}

export async function saveSubtitle(subtitleOption: SubtitleDefaultOptions) {
  setSubtitle(subtitleOption);
}

export async function savePopups(show: boolean) {
  setPopups(show);
  await saveUserSettings();
}
